// Shared "+ New transaction" / "+ Add cheque" dropdown.
// Opens a grouped menu (Money in / Money out / Transfer / Journal entry).
// Each item triggers the create-transaction flow for that type.

import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

interface NewTransactionDropdownProps {
  triggerLabel: string;
  triggerClassName: string;
  alignRight?: boolean;
}

type TxType =
  | "deposit"
  | "sales-receipt"
  | "receive-payment"
  | "refund-received"
  | "cheque"
  | "expense"
  | "bill"
  | "pay-bill"
  | "refund-given"
  | "transfer"
  | "journal-entry";

function typeLabel(t: TxType): string {
  switch (t) {
    case "deposit": return "Deposit";
    case "sales-receipt": return "Sales receipt";
    case "receive-payment": return "Receive payment";
    case "refund-received": return "Refund received";
    case "cheque": return "Cheque";
    case "expense": return "Expense";
    case "bill": return "Bill";
    case "pay-bill": return "Pay bill";
    case "refund-given": return "Refund given";
    case "transfer": return "Transfer";
    case "journal-entry": return "Journal entry";
  }
}

export default function NewTransactionDropdown({ triggerLabel, triggerClassName, alignRight }: NewTransactionDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const create = (type: TxType) => {
    setOpen(false);
    // Transaction-creation flows are wired to real routes as they ship.
    // For now, each item surfaces its type so the full UX is observable end to end.
    alert(`Open create ${typeLabel(type).toLowerCase()} flow`);
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" onClick={() => setOpen((v) => !v)} className={triggerClassName}>
        <span>{triggerLabel}</span>
        <ChevronDown size={12} strokeWidth={2.5} />
      </button>

      {open && (
        <div
          className={`absolute top-full mt-1 ${alignRight ? "right-0" : "left-0"} w-[220px] bg-white border border-line rounded-lg shadow-[0_8px_24px_rgba(14,26,26,0.14)] py-1 z-40 text-left`}
        >
          <SectionLabel>Money in</SectionLabel>
          <MenuItem label={typeLabel("deposit")} onClick={() => create("deposit")} />
          <MenuItem label={typeLabel("sales-receipt")} onClick={() => create("sales-receipt")} />
          <MenuItem label={typeLabel("receive-payment")} onClick={() => create("receive-payment")} />
          <MenuItem label={typeLabel("refund-received")} onClick={() => create("refund-received")} />

          <Divider />
          <SectionLabel>Money out</SectionLabel>
          <MenuItem label={typeLabel("cheque")} onClick={() => create("cheque")} />
          <MenuItem label={typeLabel("expense")} onClick={() => create("expense")} />
          <MenuItem label={typeLabel("bill")} onClick={() => create("bill")} />
          <MenuItem label={typeLabel("pay-bill")} onClick={() => create("pay-bill")} />
          <MenuItem label={typeLabel("refund-given")} onClick={() => create("refund-given")} />

          <Divider />
          <MenuItem label={typeLabel("transfer")} onClick={() => create("transfer")} />
          <MenuItem label={typeLabel("journal-entry")} onClick={() => create("journal-entry")} />
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 py-1.5 text-[10px] text-ink3 uppercase tracking-wider font-bold">
      {children}
    </div>
  );
}

function MenuItem({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="w-full text-left px-3 py-2 text-[12.5px] bg-transparent border-none cursor-pointer hover:bg-panel text-ink">
      {label}
    </button>
  );
}

function Divider() {
  return <div className="h-px bg-lineSoft my-1" />;
}